import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X, ShoppingCart, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import ColumnStructureDisplay from './ColumnStructureDisplay';
import { isValidFileType, getFileInputAccept } from '../utils/fileUpload';

interface ArticlesDataUploadProps {
    databaseId?: number | string | null;
    onUploadSuccess?: () => void;
}

interface UploadResult {
    message: string;
    created?: number;
    updated?: number;
    total_rows?: number;
    errors_count?: number;
    errors?: string[];
    additional_errors?: number;
    prediction_update_warning?: string;
    warnings?: string[];
    ignored_sales_columns?: string[];
}

interface UploadResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    result: UploadResult;
}

interface ColumnInfo {
  name: string;
  type: string;
  examples?: any[];
}

interface FormatModalProps {
    isOpen: boolean;
    onClose: () => void;
    databaseId?: number | string;
    columns?: ColumnInfo[];
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({ isOpen, onClose, result }) => {
    if (!isOpen) return null;

    const hasErrors = (result.errors_count || 0) > 0;
    const hasWarnings = result.errors && result.errors.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100' : 'bg-green-100'}`}>
                            {hasErrors ? (
                                <AlertTriangle className={`${hasErrors ? 'text-amber-600' : 'text-green-600'}`} size={20} />
                            ) : (
                                <CheckCircle className="text-green-600" size={20} />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Résultat de l'import des articles
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    {/* Summary Statistics */}
                    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                        <h4 className={`font-medium mb-3 ${hasErrors ? 'text-amber-900' : 'text-green-900'}`}>
                            {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé avec succès'}
                        </h4>
                        {(result.created !== undefined || result.updated !== undefined) && (
                            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                                {result.created !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                        <span className="font-medium">Articles créés:</span> {result.created}
                                    </div>
                                )}
                                {result.updated !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                        <span className="font-medium">Articles mis à jour:</span> {result.updated}
                                    </div>
                                )}
                                {result.total_rows !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                        <span className="font-medium">Total lignes:</span> {result.total_rows}
                                    </div>
                                )}
                                {result.errors_count !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                        <span className="font-medium">Erreurs:</span> {result.errors_count}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className={`p-2 bg-white bg-opacity-50 rounded border text-xs ${hasErrors ? 'text-amber-800' : 'text-green-800'}`}>
                            <strong>Résultat:</strong> {result.message}
                        </div>
                    </div>

                    {/* Sales Columns Warning */}
                    {result.warnings && result.warnings.length > 0 && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                                <Info className="text-blue-600" size={16} />
                                Avertissement
                            </h4>
                            <div className="space-y-2">
                                {result.warnings.map((warning, index) => (
                                    <p key={index} className="text-sm text-blue-800">
                                        {warning}
                                    </p>
                                ))}
                            </div>
                            {result.ignored_sales_columns && result.ignored_sales_columns.length > 0 && (
                                <p className="text-xs text-blue-700 mt-2">
                                    Utilisez l'onglet "Données de Ventes" pour importer ces données.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Prediction Update Warning */}
                    {result.prediction_update_warning && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                                <AlertTriangle className="text-amber-600" size={16} />
                                Note importante
                            </h4>
                            <p className="text-sm text-amber-800">
                                {result.prediction_update_warning}
                            </p>
                        </div>
                    )}

                    {/* Error Details */}
                    {hasWarnings && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Détails des erreurs
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {result.errors!.map((error, index) => (
                                    <div key={index} className="text-sm text-red-800 p-2 bg-white bg-opacity-50 rounded border">
                                        {error}
                                    </div>
                                ))}
                            </div>
                            
                            {result.additional_errors && result.additional_errors > 0 && (
                                <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                    <strong>Note:</strong> {result.additional_errors} erreur(s) supplémentaire(s) non affichée(s). 
                                    Consultez les logs du serveur pour la liste complète.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success Message */}
                    {!hasErrors && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                            <p className="text-purple-800 text-sm">
                                🎉 Tous les articles ont été importés avec succès ! 
                                Vous pouvez maintenant utiliser ces nouveaux articles pour enrichir vos données de ventes.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            hasErrors 
                                ? 'bg-amber-600 text-white hover:bg-amber-700' 
                                : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

const FormatModal: React.FC<FormatModalProps> = ({ isOpen, onClose, databaseId, columns }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Format fichier Articles (CSV/XLSX/XLS)</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-6">
          {/* Section importante en haut */}
          <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-amber-900 mb-2">Structure de la base</h4>
                <p className="text-sm text-amber-800">
                  Les colonnes de votre base ont été définies lors de sa création. 
                  Vos fichiers d'import doivent respecter <strong>exactement</strong> cette structure.
                </p>
              </div>
            </div>
          </div>

          {/* Afficher les colonnes existantes */}
          {columns && columns.length > 0 && (
            <div>
              <ColumnStructureDisplay 
                columns={columns}
                showExamples={true}
                compact={false}
                title="Colonnes actuelles de votre base"
              />
            </div>
          )}

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Fichiers acceptés</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li><strong>Formats:</strong> CSV, XLSX, XLS</li>
              <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
              <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
              <li><strong>Excel multi-feuilles:</strong> seule la première feuille est lue</li>
              <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Colonnes requises</h4>
            <div className="text-sm text-gray-700 space-y-2">
              <div>
                <div className="font-medium">Identifiant produit (clé unique)</div>
                <div>En-têtes acceptés (casse/accents/espaces ignorés):</div>
                <div className="p-2 bg-gray-50 rounded border text-xs font-mono">ref, id, item_code, product_id, article_id, sku</div>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>Les identifiants sont <strong>normalisés</strong>: caractères non alphanumériques supprimés (ex: "A-123" → "A123").</li>
                  <li>Les nombres sont acceptés (ex: 101 → "101" ; 101.0 → "101").</li>
                  <li>En cas de doublon avec un identifiant existant, l'article est <strong>mis à jour</strong>.</li>
                  <li>Doublons dans le même fichier: la <strong>dernière occurrence</strong> prévaut.</li>
                </ul>
              </div>
              <div>
                <div className="font-medium mt-3">Nom du produit</div>
                <div>En-têtes acceptés:</div>
                <div className="p-2 bg-gray-50 rounded border text-xs font-mono">nom, name, product_name, article_name, description, title</div>
                <div className="mt-2 text-xs text-gray-600">Astuce: évitez d'utiliser "description" comme nom si vous souhaitez aussi une colonne description distincte.</div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Colonnes optionnelles (attributs)</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Les colonnes doivent correspondre <strong>exactement</strong> aux colonnes définies lors de la création de la base.</li>
              <li>Les noms sont <strong>sanitisés</strong> en base: minuscules, accents supprimés, espaces→"_". Exemple: "Prix de vente" → "prix_de_vente".</li>
              <li>Respectez le type d'origine de la colonne de la base: valeurs numériques pour colonnes numériques, "true/false" pour booléens.</li>
              <li><strong>Les colonnes supplémentaires non présentes dans la base seront rejetées.</strong></li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Colonnes de ventes (non supportées)</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li><strong>Important:</strong> lors d'un import d'articles (cet écran), les colonnes de ventes ne sont <strong>pas supportées</strong> et généreront des erreurs.</li>
              <li>Utilisez l'écran d'import des ventes dédié pour ajouter des données de ventes.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Exemples</h4>
            <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
              id,nom,description,categorie,prix_vente,marque<br/>
              10160,Pince en bois naturel 3cm,Pince décorative en bois,Decoration,0.65,ArtCraft<br/>
              SKU-10161,Pince en bois naturel 5cm,Pince décorative en bois,Decoration,0.85,ArtCraft
            </div>
            <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto mt-3">
              item_code,name,brand,price<br/>
              A-1001,Wooden clip 3cm,ArtCraft,0.65
            </div>
          </div>

          <div className="p-3 bg-purple-50 border border-purple-200 rounded">
            <p className="text-sm text-purple-800">
              <strong>Rappels:</strong> l'ID et le nom sont obligatoires à chaque ligne. Les champs vides ne sont pas enregistrés. 
              Un identifiant déjà présent mettra à jour l'article existant. Les colonnes doivent correspondre à la structure de votre base.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Compris
          </button>
        </div>
      </div>
    </div>
  );
};

const ArticlesDataUpload: React.FC<ArticlesDataUploadProps> = ({ databaseId, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    const [databaseColumns, setDatabaseColumns] = useState<ColumnInfo[]>([]);
    const { activeDatabase, fetchDatabases } = useDatabaseContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Find the target database ID - prioritize databaseId prop over activeDatabase
    const targetDatabaseId = databaseId || activeDatabase?.id;

    // Charger les colonnes de la base de données
    useEffect(() => {
        if (targetDatabaseId) {
            api.get(API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.GET_COLUMNS(targetDatabaseId))
                .then(response => {
                    setDatabaseColumns(response.data.columns || []);
                })
                .catch(err => {
                    console.error('Error fetching database columns:', err);
                    // Ne pas bloquer si l'erreur survient
                });
        }
    }, [targetDatabaseId]);

    // Use shared file type validation

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            if (isValidFileType(selectedFile)) {
                setFile(selectedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError('Veuillez sélectionner un fichier CSV, XLSX ou XLS');
            }
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (isValidFileType(droppedFile)) {
                setFile(droppedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError('Veuillez déposer un fichier CSV, XLSX ou XLS');
            }
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const removeFile = () => {
        setFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setError('');
        setUploadResult(null);
        setShowResultModal(false);
    };

    const handleSubmitWithConfirmation = () => {
        if (!file || !targetDatabaseId) {
            setError('Veuillez sélectionner un fichier');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    const handleConfirmedSubmit = async () => {
        if (!file || !targetDatabaseId) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        setError('');
        setShowConfirmDialog(false);
        setUploadResult(null);
        setShowResultModal(false);

        try {
            const response = await api.post(API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.UPLOAD_ARTICLES_DATA(targetDatabaseId), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Assuming the API returns a result object, or create one from the response
            setUploadResult(response.data || { message: 'Articles importés avec succès !' });
            setShowResultModal(true);
            setFile(null);
            
            // Refresh databases to update row count
            fetchDatabases();
            
            // Call success callback if provided
            onUploadSuccess?.();
            
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            
        } catch (err: any) {
            const data = err?.response?.data;
            // If backend provided structured errors, show them in the modal
            if (data && (data.errors || typeof data.errors_count === 'number')) {
                const resultObj: UploadResult = {
                    message: data.error || data.message || "Erreur lors de l'import des articles.",
                    created: data.created,
                    updated: data.updated,
                    total_rows: data.total_rows,
                    errors_count: data.errors_count,
                    errors: data.errors,
                };
                setUploadResult(resultObj);
                setShowResultModal(true);
                setError('');
            } else {
                setError(data?.error || data?.message || 'Une erreur est survenue lors de l\'import');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!targetDatabaseId) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                    Aucune base de données sélectionnée. Veuillez sélectionner une base de données avant d'importer des articles.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-xl">
                        <ShoppingCart className="text-purple-600" size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Articles
                        </h2>
                        <p className="text-gray-600 text-sm">
                            Importez de nouveaux articles pour {activeDatabase?.name || 'votre base'}
                        </p>
                    </div>
                </div>
                
                <button
                    onClick={() => setShowFormatModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                >
                    <Info size={16} />
                    Format Fichier
                </button>
            </div>

            {/* Drag & Drop Area - Compact */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer group ${
                    dragActive 
                        ? 'border-purple-400 bg-purple-50' 
                        : file 
                            ? 'border-green-400 bg-green-50' 
                            : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50/30'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept={getFileInputAccept()}
                    className="hidden"
                    disabled={loading}
                />
                
                {file ? (
                    // File Selected State - Horizontal Layout
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                                <FileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile();
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-gray-600 text-xs">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        
                        {/* Import Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSubmitWithConfirmation();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm flex-shrink-0"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Upload size={16} />
                            )}
                            {loading ? 'Import...' : 'Importer'}
                        </button>
                    </div>
                ) : (
                    // Drop Zone State - Horizontal Layout
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg transition-all flex-shrink-0 ${
                            dragActive ? 'bg-purple-200' : 'bg-gray-100 group-hover:bg-purple-100'
                        }`}>
                            <ShoppingCart className={`transition-colors ${
                                dragActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-purple-500'
                            }`} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {dragActive ? 'Déposez votre fichier ici' : 'Glissez vos articles'}
                            </h3>
                            <p className="text-xs text-gray-600">
                                ou <span className="text-purple-600 font-medium">cliquez pour parcourir</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX, XLS</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <AlertTriangle className="text-purple-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer l'import
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir importer ces articles ? 
                            Cette opération enrichira votre base de données avec les nouveaux produits.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmedSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                Confirmer l'import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Format Modal */}
            <FormatModal 
                isOpen={showFormatModal} 
                onClose={() => setShowFormatModal(false)}
                databaseId={targetDatabaseId}
                columns={databaseColumns}
            />

            {/* Upload Result Modal */}
            {uploadResult && (
                <UploadResultModal 
                    isOpen={showResultModal}
                    onClose={handleCloseResultModal}
                    result={uploadResult}
                />
            )}

            {/* Error Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                    <X className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default ArticlesDataUpload;
